/**
 * Utility functions for converting data to CSV format
 */

import { EMPTY_VALUE } from './format';

/**
 * Flattens a nested object into a single-level object with dot notation keys.
 *
 * @param obj - The object to flatten
 * @param prefix - Optional prefix for keys (used internally for recursion)
 * @returns Flattened object with dot notation keys
 *
 * @example
 * flattenObject({ customer: { name: 'John', email: 'j@ex.com' } })
 * // Returns: { 'customer.name': 'John', 'customer.email': 'j@ex.com' }
 *
 * @example
 * flattenObject({ tags: ['a', 'b', 'c'] })
 * // Returns: { tags: 'a;b;c' }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function flattenObject(obj: any, prefix: string = ''): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flattened: Record<string, any> = {};

  // Handle null or undefined
  if (obj == null) {
    return flattened;
  }

  // Handle primitives
  if (typeof obj !== 'object') {
    return { [prefix]: obj };
  }

  // Handle arrays - join with semicolons
  if (Array.isArray(obj)) {
    // If array contains objects, stringify them
    if (obj.length > 0 && typeof obj[0] === 'object' && obj[0] !== null) {
      return { [prefix]: obj.map((item) => JSON.stringify(item)).join(';') };
    }
    // For primitive arrays, join with semicolons
    return { [prefix]: obj.join(';') };
  }

  // Handle objects - recursively flatten
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      // Handle null or undefined values
      if (value == null) {
        flattened[newKey] = '';
        continue;
      }

      // Handle arrays
      if (Array.isArray(value)) {
        // If array contains objects, stringify them
        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
          flattened[newKey] = value.map((item) => JSON.stringify(item)).join(';');
        } else {
          // For primitive arrays, join with semicolons
          flattened[newKey] = value.join(';');
        }
        continue;
      }

      // Handle nested objects
      if (typeof value === 'object' && !(value instanceof Date)) {
        const nested = flattenObject(value, newKey);
        Object.assign(flattened, nested);
        continue;
      }

      // Handle primitives (including Dates)
      flattened[newKey] = value;
    }
  }

  return flattened;
}

const PLAIN_NUMBER = /^[+-]?\d+(\.\d+)?$/;

/**
 * Money formatters (`formatCurrency`, `createFormatMoney`) decorate a plain number with a currency
 * symbol, thousands separators, and/or a trailing ISO/token code — e.g. `-$25.50 USD` or
 * `-25.50 USDC`. Stripping that decoration lets a formatted negative amount round-trip through the
 * same numeric exemption as a raw one below.
 */
function stripMoneyDecoration(value: string): string {
  return value
    .replace(/\s*[A-Za-z]{2,6}$/, '')
    .replace(/[$€£¥]/g, '')
    .replace(/,/g, '')
    .trim();
}

/**
 * A plain number (optionally signed/decimal, optionally money-formatted) isn't a spreadsheet
 * formula, even though it can start with `+`/`-` — e.g. a negative decimal amount in a rewards
 * CSV, a formatted currency amount in a card-transactions export, or the `EMPTY_VALUE` placeholder
 * used across dashboard export templates. Escaping these would corrupt otherwise-legitimate,
 * machine-parseable or customer-facing data for no security benefit.
 */
function isSafeNumericish(value: string): boolean {
  return value === EMPTY_VALUE || PLAIN_NUMBER.test(stripMoneyDecoration(value));
}

/**
 * Escapes a value for safe use in CSV format.
 * - Neutralizes formula injection by prefixing a leading `=`, `+`, `-`, `@`, tab, or CR with a
 *   `'` — spreadsheet apps (Excel, Sheets) read a cell starting with one of those as a formula,
 *   which is dangerous when the value came from untrusted input (a counterparty, a customer).
 *   Exempts plain numbers (raw or money-formatted) and the `EMPTY_VALUE` placeholder — see
 *   {@link isSafeNumericish}.
 * - Wraps in quotes if contains comma, newline, CR, or quote — a bare CR alone can terminate a
 *   CSV record in some parsers, so it must be quoted the same as `\n`.
 * - Doubles any quotes inside the value
 * - Converts null/undefined to empty string
 *
 * @param value - The value to escape
 * @returns Escaped string safe for CSV
 *
 * @example
 * escapeCSVValue('Hello, World') // Returns: '"Hello, World"'
 * escapeCSVValue('Say "Hi"') // Returns: '"Say ""Hi"""'
 * escapeCSVValue(null) // Returns: ''
 * escapeCSVValue('=cmd|"/C calc"!A0') // Returns: "'=cmd|\"\"/C calc\"\"!A0"
 * escapeCSVValue('-5.00') // Returns: '-5.00' (plain number, not escaped)
 * escapeCSVValue('--') // Returns: '--' (EMPTY_VALUE placeholder, not escaped)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function escapeCSVValue(value: any): string {
  // Handle null and undefined
  if (value == null) {
    return '';
  }

  // Convert to string
  let stringValue = String(value);

  if (/^[=+\-@\t\r]/.test(stringValue) && !isSafeNumericish(stringValue)) {
    stringValue = `'${stringValue}`;
  }

  // Check if we need to wrap in quotes
  const needsQuotes =
    stringValue.includes(',') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r') ||
    stringValue.includes('"');

  if (needsQuotes) {
    // Double any existing quotes
    stringValue = stringValue.replace(/"/g, '""');
    // Wrap in quotes
    return `"${stringValue}"`;
  }

  return stringValue;
}

/**
 * Converts a header row plus pre-built string rows into a CSV string.
 * - Emits the header row first, then each data row in order
 * - Escapes every cell with `escapeCSVValue`
 * - Does not pad or truncate rows; callers own column alignment
 *
 * @param headers - Column header labels, in output order
 * @param rows - Data rows, each an array of cell values in the same column order
 * @returns CSV string with header row and data rows joined by newlines
 *
 * @example
 * rowsToCSV(['ID', 'Name'], [['1', 'John'], ['2', 'Doe, Jane']])
 * // Returns:
 * // ID,Name
 * // 1,John
 * // 2,"Doe, Jane"
 */
export function rowsToCSV(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  return [headers, ...rows].map((row) => row.map(escapeCSVValue).join(',')).join('\n');
}

/**
 * Converts an array of objects to CSV string format.
 * - Automatically flattens nested objects using dot notation
 * - Extracts all unique column names from all objects
 * - Handles missing fields with empty cells
 * - Properly escapes special characters
 *
 * @param data - Array of objects to convert to CSV
 * @returns CSV string with header row and data rows
 *
 * @example
 * arrayToCSV([
 *   { id: '1', name: 'John', customer: { email: 'j@ex.com' } },
 *   { id: '2', name: 'Jane', customer: { email: 'jane@ex.com' } }
 * ])
 * // Returns:
 * // id,name,customer.email
 * // 1,John,j@ex.com
 * // 2,Jane,jane@ex.com
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function arrayToCSV(data: any[]): string {
  // Handle empty array
  if (!Array.isArray(data) || data.length === 0) {
    return '';
  }

  // Flatten all objects and collect all unique keys
  const flattenedData = data.map((item) => flattenObject(item));
  const allKeys = new Set<string>();

  flattenedData.forEach((item) => {
    Object.keys(item).forEach((key) => allKeys.add(key));
  });

  // Sort keys alphabetically for consistent output
  const columns = Array.from(allKeys).sort();

  // Create header row
  const header = columns.map((col) => escapeCSVValue(col)).join(',');

  // Create data rows
  const rows = flattenedData.map((item) => {
    return columns.map((col) => escapeCSVValue(item[col])).join(',');
  });

  // Combine header and rows with newlines
  return [header, ...rows].join('\n');
}

/**
 * Converts an array of pre-formatted objects to CSV string format.
 * - Takes already-formatted data (no flattening or transformation)
 * - Uses specified column order for CSV output
 * - Properly escapes special characters
 *
 * This function is designed for use with export templates that have already
 * formatted the data to match table UI columns.
 *
 * @param data - Array of pre-formatted row objects
 * @param columnOrder - Array of column keys in desired order
 * @returns CSV string with header row and data rows
 *
 * @example
 * ```typescript
 * formattedArrayToCSV(
 *   [
 *     { 'Date added': '01/15/25', 'Customer': 'John Doe', 'Amount': '$100.00' },
 *     { 'Date added': '01/16/25', 'Customer': 'Jane Smith', 'Amount': '$250.00' }
 *   ],
 *   ['Date added', 'Customer', 'Amount']
 * )
 * // Returns:
 * // Date added,Customer,Amount
 * // 01/15/25,John Doe,$100.00
 * // 01/16/25,Jane Smith,$250.00
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formattedArrayToCSV(data: Record<string, any>[], columnOrder: string[]): string {
  // Handle empty array
  if (!Array.isArray(data) || data.length === 0) {
    return '';
  }

  // Handle empty column order
  if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
    return '';
  }

  // Create header row from column order
  const header = columnOrder.map((col) => escapeCSVValue(col)).join(',');

  // Create data rows using specified column order
  const rows = data.map((item) => {
    return columnOrder.map((col) => escapeCSVValue(item[col])).join(',');
  });

  // Combine header and rows with newlines
  return [header, ...rows].join('\n');
}
